import { useApolloClient } from '@apollo/react-hooks';
import AddGuestComponent from '../../libs/gs-component-add-guest';
import produce from 'immer';
import React from 'react';

import { HouseholdQuery, useEventsQuery, useWeddingWebsiteQuery } from '../../generated/graphql';
import { householdQuery } from '../WeddingPageCard/RsvpInfo';
import { withTypename } from './helper';
import { findLeader, getEditLabel } from '../../helpers/households';
import useShareTracking from './useShareTracking';

export type Household = HouseholdQuery['households'][0];

interface Props {
  guestRailOpen: boolean;
  setGuestRailOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editHousehold?: Household;
  setEditHousehold: React.Dispatch<React.SetStateAction<Household | undefined>>;
  setHouseholdsList: React.Dispatch<React.SetStateAction<Household[] | undefined>>;
  sequence: string;
  selectedHouseholds: Set<string>;
  setSelectedHouseholds: React.Dispatch<React.SetStateAction<Set<string>>>;
  householdsList?: any;
  messageMedium: 'email' | 'sms';
  messageType: string | undefined;
}

const AddGuestRail: React.FC<Props> = ({
  guestRailOpen,
  setGuestRailOpen,
  editHousehold,
  setEditHousehold,
  setHouseholdsList,
  sequence = 'email',
  selectedHouseholds,
  setSelectedHouseholds,
  householdsList,
  messageMedium,
  messageType,
}) => {
  const track = useShareTracking();
  const { data: eventsData } = useEventsQuery();
  const { data: wwsData } = useWeddingWebsiteQuery();
  const editLabel = getEditLabel(sequence, !!householdsList?.length);
  const client = useApolloClient();

  const wedding = wwsData?.wedding;
  const guestWeddingQuestions = wwsData?.guestWedding?.questions || null;
  const events = eventsData?.events;

  const trackBulkEditSubmission = () => {
    track('Guest Messaging Interaction', {
      action: messageMedium === 'email' ? 'bulk update email' : 'bulk update phone number',
      messageMedium: messageMedium,
      messageType: messageType,
    });
  };

  const onBulkSuccess = (householdsList: Household[]) => {
    trackBulkEditSubmission();
    householdsList.map(household => onSuccess(household));
  };

  const onSuccess = (household: Household) => {
    const { households } = client.readQuery<HouseholdQuery>({
      query: householdQuery,
    })!;

    const updated = produce(households, householdsDraft => {
      const householdWithAllFields = withTypename(household);
      const index = householdsDraft.findIndex(h => h.id === household.id);

      // If index is found then update the array of households in cache
      if (index >= 0) {
        householdsDraft[index] = householdWithAllFields;
      } else {
        // Otherwise, we are adding the household.
        householdsDraft.push(householdWithAllFields);
      }
    });

    client.writeQuery({
      query: householdQuery,
      data: {
        households: updated,
      },
    });

    const leaderId = findLeader(household)?.id || '';
    if (leaderId) {
      selectedHouseholds.add(leaderId);
      setSelectedHouseholds(selectedHouseholds);
    }
  };

  const onDelete = (household: Household) => {
    const { households } = client.readQuery<HouseholdQuery>({
      query: householdQuery,
    })!;

    const updated = produce(households, householdsDraft => {
      const index = householdsDraft.findIndex(h => h.id === household.id);

      // If index is found then update the array of households in cache.
      if (index >= 0) {
        householdsDraft.splice(index, 1);
      }
    });

    client.writeQuery({
      query: householdQuery,
      data: {
        households: updated,
      },
    });
  };

  if (events && wedding) {
    return (
      <AddGuestComponent
        isOpen={guestRailOpen}
        overlay={false}
        contactFocus={sequence}
        onClose={() => {
          setGuestRailOpen(false);
          setEditHousehold(undefined);
          setHouseholdsList(undefined);
        }}
        onSuccess={onSuccess as any}
        onBulkSuccess={onBulkSuccess as any}
        onDelete={() => {
          onDelete(editHousehold as any);
          setGuestRailOpen(false);
        }}
        events={events as any[]}
        wedding={wedding}
        household={editHousehold as any}
        topLabel={editHousehold || householdsList ? editLabel : 'Add a guest'}
        guestWeddingQuestions={guestWeddingQuestions as any}
        householdsList={householdsList}
      />
    );
  }

  return null;
};

export default AddGuestRail;
